import { logger } from "../services/logger.ts";
import React, { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Search, Shield, Phone } from 'lucide-react';
import { supabase } from "../lib/db.ts";

interface UserAddModalProps {
  onClose: () => void;
  onAdd: () => void;
}

interface UserSearchResult {
  id: string;
  email: string;
  role: string;
}

// Define special characters regex pattern (same as UserRegistration)
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{};':|<>?,./`~]/;

export function UserAddModal({ onClose, onAdd }: UserAddModalProps) {
  const [mode, setMode] = useState<'create' | 'search'>('search');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  
  // Create new user states - updated to match UserRegistration.tsx
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: 'user'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check minimum length
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter (a-z)');
    }
    
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter (A-Z)');
    }
    
    // Check for digit
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number (0-9)');
    }
    
    // Check for special character
    if (!SPECIAL_CHARS_REGEX.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (SPECIAL_CHARS_REGEX.test(password)) strength++;
    
    if (strength <= 2) return { strength: 1, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 2, label: 'Fair', color: 'bg-wayja-teal' };
    if (strength <= 5) return { strength: 3, label: 'Good', color: 'bg-yellow-500' };
    return { strength: 4, label: 'Strong', color: 'bg-green-500' };
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    // Use the new password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors.join('. ');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    
    setLoading(true);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.search;
      return newErrors;
    });
    
    try {
      // Use the new database function
      const { data, error: searchError } = await supabase
        .rpc('admin_search_users', { search_email: searchEmail });
      
      if (searchError) throw searchError;

      if (!Array.isArray(data)) {
        throw new Error('Invalid response format');
      }
      
      setSearchResults(data);
      
      if (data.length === 0) {
        setErrors(prev => ({ ...prev, search: 'No users found' }));
      }
    } catch (err) {
      logger.error('Error searching users:', err);
      setErrors(prev => ({ ...prev, search: err instanceof Error ? err.message : 'Failed to search users' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.submit;
      return newErrors;
    });

    try {
      // Log request parameters for debugging
      logger.debug('Creating user with params:', {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        role: formData.role
      });

      // Create a separate Supabase client instance to avoid affecting the current admin session
      const { createClient } = await import('@supabase/supabase-js');
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      // Create isolated client that won't affect current session
      const isolatedClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: false, // Don't persist this session
          autoRefreshToken: false, // Don't auto-refresh
          detectSessionInUrl: false // Don't detect session in URL
        }
      });

      // Use the isolated client to create the user without affecting admin session
      const { data, error: createError } = await isolatedClient.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`,
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
          }
        }
      });

      if (createError) {
        logger.error('User creation error:', createError);
        throw createError;
      }

      if (!data.user) {
        throw new Error('User creation failed - no user data returned');
      }

      // If role is not 'user', update the role using the admin function (with main client)
      if (formData.role !== 'user') {
        const { error: roleError } = await supabase.rpc('admin_update_user_role', {
          target_user_id: data.user.id,
          new_role: formData.role
        });

        if (roleError) {
          logger.warn('Failed to update user role:', roleError);
          // Don't fail the entire creation if role update fails
        }
      }

      logger.info('Admin user creation successful:', {
        userId: data.user.id,
        email: data.user.email,
        role: formData.role
      });

      onAdd();
      onClose();
    } catch (err) {
      logger.error('Error creating user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.submit;
      return newErrors;
    });

    try {
      // Use the new database function
      const { error: updateError } = await supabase.rpc('admin_update_user_role', {
        target_user_id: selectedUser.id,
        new_role: formData.role
      });

      if (updateError) throw updateError;

      onAdd();
      onClose();
    } catch (err) {
      logger.error('Error updating role:', err);
      setErrors(prev => ({ ...prev, submit: err instanceof Error ? err.message : 'Failed to update user role' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <User className="w-5 h-5 text-wayja-teal" />
            {mode === 'create' ? 'Add New User' : 'Find User'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMode('search')}
            className={`flex-1 py-2 rounded-md font-medium transition-colors ${
              mode === 'search' 
                ? 'bg-wayja-teal text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Find Existing
          </button>
          <button
            onClick={() => setMode('create')}
            className={`flex-1 py-2 rounded-md font-medium transition-colors ${
              mode === 'create' 
                ? 'bg-wayja-teal text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Create New
          </button>
        </div>

        {(errors.submit || errors.search) && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errors.submit || errors.search}
          </div>
        )}

        {mode === 'search' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search by Email
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="Enter email to search"
                />
              </div>
            </div>

            <button
              onClick={handleSearch}
              disabled={loading || !searchEmail.trim()}
              className="w-full bg-wayja-teal text-white py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>

            {searchResults.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="font-medium text-gray-700">Search Results</h4>
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    onClick={() => {
                      setSelectedUser(result);
                      setFormData(prev => ({ ...prev, role: result.role }));
                    }}
                    className={`p-3 rounded-md cursor-pointer transition-colors ${
                      selectedUser?.id === result.id
                        ? 'bg-wayja-teal/10 border border-wayja-teal'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{result.email}</span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-200 text-gray-800">
                        <Shield className="w-4 h-4 mr-1" />
                        {result.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedUser && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Update Role
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                    disabled={loading}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <button
                  onClick={handleUpdateRole}
                  disabled={loading || formData.role === selectedUser.role}
                  className="w-full bg-wayja-teal text-white py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Updating...' : 'Update Role'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`pl-10 w-full rounded-md border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                    placeholder="John"
                    disabled={loading}
                  />
                </div>
                {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`pl-10 w-full rounded-md border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                    placeholder="Doe"
                    disabled={loading}
                  />
                </div>
                {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 w-full rounded-md border ${errors.email ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                  placeholder="john.doe@example.com"
                  disabled={loading}
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`pl-10 w-full rounded-md border ${errors.phone ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                  placeholder="+27 12 345 6789"
                  disabled={loading}
                />
              </div>
              {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  className={`pl-10 pr-10 w-full rounded-md border ${errors.password ? 'border-red-500' : 'border-gray-300'} text-gray-700 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
              
              {/* Password strength indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password strength</span>
                    <span className="text-xs font-medium text-gray-700">{getPasswordStrength(formData.password).label}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${getPasswordStrength(formData.password).color}`}
                      style={{ width: `${getPasswordStrength(formData.password).strength * 25}%` }}
                    ></div>
                  </div>
                  
                  {/* Password requirements checklist */}
                  <ul className="mt-2 text-xs space-y-1">
                    <li className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{formData.password.length >= 8 ? '✓' : '○'}</span>
                      At least 8 characters
                    </li>
                    <li className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{/[a-z]/.test(formData.password) ? '✓' : '○'}</span>
                      One lowercase letter
                    </li>
                    <li className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                      One uppercase letter
                    </li>
                    <li className={`flex items-center ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                      One number
                    </li>
                    <li className={`flex items-center ${SPECIAL_CHARS_REGEX.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{SPECIAL_CHARS_REGEX.test(formData.password) ? '✓' : '○'}</span>
                      One special character
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                disabled={loading}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <button
              type="submit"
              className="w-full bg-wayja-teal text-white py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50 transition-colors"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}